import { GearItem, GearStat, raiseCap, raiseCostAt, sameOwner } from "@highmarch/domain";
import { GearCompare, compareOn, itemScore } from "@highmarch/app";
import { RARITY_LABEL, SLOT_GLYPH, fmtStat, pct } from "../gearUi";
import { tickNumber } from "../kit";
import { toast } from "../toast";
import type { ArmouryCtx, OwnerView } from "./host";
import { openItemCard } from "./itemCard";
import { btn, contribution, costEl, essenceEl, essenceShortfall, faceEl, gearImg, h, ico, isNew, ownerLevel, ownerName, ownerViews, pctOrDash, powerChip, rarityTag, refusal, shortfallText, signed, signedPct, slotEmptyEl, slotTitle, statLabel, tag, tagIcon, viewOf } from "./itemBits";
import { equipItem, upgradeItem } from "./itemActions";
import { burst, flyTo, pop, shine } from "./itemFx";
import { closeButton, mountPanel, onRefresh, swipeToClose } from "./itemPanels";
import { PickRow, WEAKER_SHOWN, pickerModel } from "./pickerModel";

export { WEAKER_SHOWN, pickerModel, type PickRow, type PickerModel } from "./pickerModel";

/**
 * The socket picker (mockup2): one socket's items for one owner — the socket is the filter, so the question is only
 * "which item?". Groups: Worn (its primary action is Upgrade +1), Better, Needs <Tower> Lv N (with the way to the
 * Towers tab), Weaker. Every row carries its gear delta on this owner; the picked row opens a Worn / This / Δ table
 * from `compareOn` (affinity ★, affixes, the Epic set, Mythic active or dead here) with what else the swap does and
 * Details / Equip on <owner>. A bottom panel inside the Armoury sheet.
 */

/** How long the picker stays after an equip lands, so the socket tab visibly takes the item. */
const EQUIP_LINGER_MS = 420;
const TENTHS = 1000;

const fmtTenths = (t: number): string => (t >= 100 ? `+${Math.round(t / 10)}%` : `+${(t / 10).toFixed(1)}%`);

/**
 * Opens the picker on `owner`'s socket `slot` (a child of ctx.sheet). `focusItem` opens with that item's compare
 * expanded (the "For X" strip's tap). (The optional 4th argument is an addition to the contract.)
 */
export function openSocketPicker(ctx: ArmouryCtx, owner: OwnerView, slot: number, focusItem?: number): void {
  const B = ctx.B, profile = ctx.profile, who = owner.owner;
  const panel = mountPanel(ctx, "apick");
  const root = panel.root;
  let curSlot = slot;
  let open: number | null = focusItem ?? null;
  let weakerOpen = false;
  let busy = false;
  const head = h("div", "phead");
  const tabs = h("div", "stabs");
  const list = h("div", "plist");
  const grab = h("div", "grab");
  const handle = h("div", "phandle", grab, head);
  root.append(handle, tabs, list);
  swipeToClose(panel, handle);

  const name = ownerName(B, who);
  const slotsOfKind = B.gear.slots.filter((s) => s.target === who.target);

  const render = (scrollToOpen = false) => {
    const views = ownerViews(ctx);
    const view = viewOf(views, who) ?? owner;
    const m = pickerModel(profile, who, curSlot);
    const slotDef = B.gear.slots[curSlot];

    // Header: whose socket, and how much there is to choose from.
    const counts = [`${m.fit} item${m.fit === 1 ? "" : "s"} fit`];
    if (m.needCount > 0) counts.push(`${m.needCount} need${m.needCount === 1 ? "s" : ""} Lv ${m.needs[0].level}${m.needs.length > 1 ? "+" : ""}`);
    head.replaceChildren(faceEl(B, who), h("div", "tt", h("b", "disp", `${name} · ${slotDef.name}`), h("small", "", `${statLabel(slotDef.primary)} slot · ${counts.join(", ")}`)), closeButton(panel.close));

    // Socket tabs: switch slot without leaving.
    tabs.replaceChildren(...slotsOfKind.map((s) => {
      const sv = view.sockets.find((x) => x.slot === s.index);
      const worn = sv?.worn ?? null;
      const t = btn(`st${s.index === curSlot ? " on" : ""}`, worn ? gearImg(B, worn, 32) : ico(SLOT_GLYPH[s.id] ?? "bag", 16, "e"), h("span", "", s.name));
      t.dataset.slot = String(s.index);
      if ((sv?.better ?? 0) > 0 && s.index !== curSlot) t.append(h("span", "dot", String(sv!.better)));
      t.addEventListener("click", () => { if (curSlot === s.index) return; curSlot = s.index; open = null; weakerOpen = false; render(); list.scrollTop = 0; });
      return t;
    }));

    // Rows.
    const out: HTMLElement[] = [];
    // Nothing at all for this slot: one empty state (the slot's own ghosted icon) instead of an empty-socket line too.
    const none = m.worn === null && m.fit === 0 && m.needCount === 0;
    if (m.worn !== null) out.push(row(m.worn, "worn"));
    else if (!none) out.push(h("div", "pempty", ico(SLOT_GLYPH[slotDef.id] ?? "bag", 18), `${slotDef.name} socket is empty`));
    if (m.better.length > 0) { out.push(groupHead("Better", "up", m.better.length)); for (const r of m.better) out.push(row(r, "better")); }
    for (const g of m.needs) {
      out.push(groupHead(`Needs ${name} Lv ${g.level}`, "lock", null));
      for (const r of g.rows) out.push(row(r, "needs"));
    }
    if (m.needs.length > 0) {
      const link = btn("alink", `${name} is Lv ${ownerLevel(profile, who)} · upgrade in Towers`, ico("chev", 14));
      link.addEventListener("click", () => ctx.openTowers());
      out.push(link);
    }
    if (m.weaker.length > 0) {
      out.push(groupHead("Weaker", "dn", m.weaker.length));
      const shown = weakerOpen ? m.weaker : m.weaker.slice(0, WEAKER_SHOWN);
      for (const r of shown) out.push(row(r, "weaker"));
      if (shown.length < m.weaker.length) {
        const more = btn("amore", `${m.weaker.length - shown.length} more weaker`, ico("chev", 14));
        more.addEventListener("click", () => { weakerOpen = true; render(); });
        out.push(more);
      }
    }
    if (none) out.push(slotEmptyEl(B, curSlot, `No ${slotDef.name} yet — clear levels to earn gear chests`));
    list.replaceChildren(...out);
    if (scrollToOpen) list.querySelector<HTMLElement>(".prow.sel")?.scrollIntoView?.({ block: "center" });

    function groupHead(text: string, kind: string, n: number | null): HTMLElement {
      return h("div", `gh ${kind}`, ico(kind === "lock" ? "lock" : "chev", 12), text, n === null ? null : h("span", "n", String(n)), h("span", "ln"));
    }

    function row(r: PickRow, kind: "worn" | "better" | "needs" | "weaker"): HTMLElement {
      const it = r.item, c = r.cmp;
      const el = h("div", `prow ${kind} r${it.rarity}${open === it.id ? " sel" : ""}`);
      el.dataset.id = String(it.id);
      el.tabIndex = 0;
      el.setAttribute("role", "button");
      const giw = h("span", "giw", gearImg(B, it, 50), h("span", "pl", `Lv ${it.level}`));
      const title = h("b", "", rarityTag(it), h("span", "nm", slotTitle(B, it)));
      if (isNew(ctx, it)) title.append(tag("NEW", "new"));
      if (kind === "worn") title.append(tag("WORN", "worn"));
      else if (it.equippedTo !== null) { const on = tag(`on ${ownerName(B, it.equippedTo)}`, "from"); on.prepend(faceEl(B, it.equippedTo, "mini")); title.append(on); }
      if (c.needLevel !== null) title.append(tagIcon("lock", `Lv ${c.needLevel}`, "lock"));
      if (it.locked) title.append(tagIcon("lock", "", "lockd"));
      const primary = h("span", "v", pct(c.primary.after));
      const ln1 = h("div", "ln1", primary, ` ${fmtStat(c.primary.stat)}`, c.primary.affinity ? h("span", "star", " ★") : null);
      const extras = it.affixes.map((a) => `${pct(a.value)} ${fmtStat(a.stat)}`);
      extras.push(`Lv ${it.level}`);
      const ln2 = h("div", "ln2", extras.join(" · "));
      if (c.mythic !== null) ln1.append(h("span", `myth${c.mythic.active ? "" : " off"}`, ` ✦ ${B.gear.mythicEffects[c.mythic.effect]?.name ?? ""}`));
      const rt = h("div", "rt", kind === "worn" ? powerChip(itemScore(B, it), true) : powerChip(c.power));
      if (kind === "worn") rt.append(upgradeButton(it));
      el.append(giw, h("div", "mid", title, ln1, ln2), rt);
      el.addEventListener("click", () => { open = open === it.id ? null : it.id; render(); });
      el.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") { open = open === it.id ? null : it.id; render(); } });
      if (open === it.id) el.append(kind === "worn" ? wornBlock(r) : compareBlock(r, views));
      return el;
    }
  };

  /** Upgrade +1 on the worn row (graft 4: the worn item's primary action; repeatable, stays under the thumb). */
  function upgradeButton(it: GearItem): HTMLElement {
    if (it.level >= raiseCap(profile, it)) return h("span", "maxed", `Lv ${it.level} max`);
    const cost = profileCost(it);
    const short = essenceShortfall(cost, profile.forge.essence);
    const b = btn(`abtn blue qe${short ? " short" : ""}`, h("span", "disp", `Lv ${it.level + 1}`), essenceEl(cost, profile.forge.essence));
    b.setAttribute("aria-label", `Raise to Lv ${it.level + 1}`);
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      const before = profile.findGear(it.id);
      const was = before === null ? null : compareOn(profile, before, who).primary.after;
      const r = upgradeItem(ctx, it);
      if (r !== true) { toast(ctx.toastParent, r === "not enough essence" ? `Raise a level: ${essenceShortfall(profileCost(it), profile.forge.essence)}` : refusal(r)); return; }
      panel.keep();
      render();
      const worn = list.querySelector<HTMLElement>(".prow.worn");
      if (worn === null) return;
      pop(worn.querySelector(".pl"));
      shine(worn.querySelector<HTMLElement>(".giw")!);
      const v = worn.querySelector(".ln1 .v");
      const now = profile.findGear(it.id);
      if (v !== null && was !== null && now !== null) {
        const to = compareOn(profile, now, who).primary.after;
        tickNumber(v, Math.round(was.toNumber() * TENTHS), Math.round(to.toNumber() * TENTHS), 450, fmtTenths);
      }
    });
    return b;
  }
  const profileCost = (it: GearItem) => raiseCostAt(B, it.rarity, it.level);

  /** The worn row, opened: what it gives here, and Details / Take off. */
  function wornBlock(r: PickRow): HTMLElement {
    const gives = contribution(B, r.item, who);
    const table = h("table", "", h("tr", "", h("th", "", `On ${name}`), h("th", "", "Gives")));
    gives.forEach((v, s) => { if (!v.isZero()) table.append(h("tr", "", h("td", "", statLabel(s as GearStat)), h("td", "", pct(v)))); });
    table.append(h("tr", "tot", h("td", "", "Gear"), h("td", "", `⚡${itemScore(B, r.item)}`)));
    const box = h("div", "cmp", table, mythicLines(r.cmp));
    const details = btn("abtn parch", h("span", "disp", "Details"));
    details.addEventListener("click", (e) => { e.stopPropagation(); openItemCard(ctx, r.item, { owner: who }); });
    const off = btn("abtn parch", h("span", "disp", "Unequip"));
    off.addEventListener("click", (e) => {
      e.stopPropagation();
      const res = profile.unequipGear(r.item.id);
      if (res !== true) { toast(ctx.toastParent, refusal(res)); return; }
      ctx.changed(); panel.keep(); open = null; render();
    });
    box.append(h("div", "acts", details, off));
    box.addEventListener("click", (e) => e.stopPropagation());
    return box;
  }

  function mythicLines(c: GearCompare): HTMLElement | null {
    const out = h("div", "myths");
    if (c.mythic !== null) {
      const def = B.gear.mythicEffects[c.mythic.effect];
      out.append(c.mythic.active ? h("div", "amyth on", ico("star", 12), h("span", "t", h("b", "", def?.name ?? ""), ` ${def?.description ?? ""}`))
        : h("div", "amyth off", ico("star", 12), `${def?.name ?? ""} does nothing on ${name}`));
    }
    if (c.mythicLost !== null && c.mythicLost.active) out.append(h("div", "amyth lost", ico("star", 12), `Loses ${B.gear.mythicEffects[c.mythicLost.effect]?.name ?? ""}`));
    return out.childNodes.length > 0 ? out : null;
  }

  /** Worn / This / Δ on this owner, what else the swap does, and Details / Equip on <owner>. */
  function compareBlock(r: PickRow, views: readonly OwnerView[]): HTMLElement {
    const c = r.cmp, it = r.item;
    const worn = contribution(B, c.worn, who), mine = contribution(B, it, who);
    const table = h("table", "", h("tr", "", h("th", "", `On ${name}`), h("th", "", "Worn"), h("th", "", "This"), h("th", "", "Δ")));
    for (let s = 0; s < mine.length; s++) {
      if (worn[s].isZero() && mine[s].isZero()) continue;
      const d = mine[s].sub(worn[s]);
      const thisCell = h("td", "", pctOrDash(mine[s]));
      if (c.primary.affinity && s === c.primary.stat) thisCell.append(h("span", "star", " ★"));
      table.append(h("tr", "", h("td", "", statLabel(s as GearStat)), h("td", "", pctOrDash(worn[s])), thisCell, h("td", d.isPositive() ? "u" : d.isNegative() ? "d" : "", signedPct(d))));
    }
    if (c.setBefore !== c.setAfter) {
      const setWords = `${pct(B.gear.setDamage)} dmg & range`;
      table.append(h("tr", "set", h("td", "", "Epic set"), h("td", "", c.setBefore ? "on" : "—"), h("td", "", c.setAfter ? "on" : "—"),
        h("td", c.setAfter ? "u" : "d", c.setAfter ? setWords : `−${setWords.slice(1)}`)));
    }
    const wornScore = c.worn === null ? 0 : itemScore(B, c.worn);
    table.append(h("tr", "tot", h("td", "", "Gear"), h("td", "", c.worn === null ? "—" : `⚡${wornScore}`), h("td", "", `⚡${itemScore(B, it)}`), h("td", c.power > 0 ? "u" : c.power < 0 ? "d" : "", signed(c.power))));
    const box = h("div", "cmp", table, mythicLines(c));

    // What else happens: where it comes from, where the old one goes, sets, binding.
    const notes: { text: string; warn: boolean }[] = [];
    if (c.takesFrom !== null) {
      const from = viewOf(views, c.takesFrom), fromName = ownerName(B, c.takesFrom);
      const lvl = ctx.scope.level;
      if (from?.seat && !sameOwner(c.takesFrom, who)) notes.push({ text: `${fromName} loses ⚡${itemScore(B, it)}${lvl !== undefined ? ` for Level ${lvl}` : ""}.`, warn: true });
      else notes.push({ text: `Takes it off ${fromName}${lvl !== undefined ? ` (bench, not in Level ${lvl})` : ""}.`, warn: false });
      if (c.takesFromBreaksSet) notes.push({ text: `Breaks ${fromName}'s Epic set.`, warn: true });
    }
    if (c.toStash !== null) notes.push({ text: `Your ${RARITY_LABEL[c.toStash.rarity]} ${slotTitle(B, c.toStash)} goes to the stash.`, warn: false });
    if (c.binds) notes.push({ text: `Raised ${B.gear.bindAboveRaised + 1}+ levels, so equipping binds it: no trading it later.`, warn: true });
    if (notes.length > 0) box.append(h("div", `note${notes.some((n) => n.warn) ? " warn" : ""}`, ico("flag", 13), h("span", "", notes.map((n) => n.text).join(" "))));

    const details = btn("abtn parch", h("span", "disp", "Details"));
    details.addEventListener("click", (e) => { e.stopPropagation(); openItemCard(ctx, it, { owner: who }); });
    let go: HTMLButtonElement;
    if (c.canWear === true) {
      go = btn("abtn gold go", h("span", "disp", `Equip on ${name}`));
      go.addEventListener("click", (e) => {
        e.stopPropagation();
        if (busy) return;
        const icon = box.parentElement?.querySelector<HTMLElement>(".giw") ?? null;
        const mythicActive = c.mythic?.active === true;
        if (!equipItem(ctx, it, who, mythicActive)) return;
        busy = true;
        panel.keep();
        const land = () => {
          open = null;
          render();
          const t = tabs.querySelector<HTMLElement>(`.st[data-slot="${it.slot}"]`);
          if (t !== null) burst(t, it.rarity);
          window.setTimeout(panel.close, EQUIP_LINGER_MS);
        };
        const tab = tabs.querySelector<HTMLElement>(`.st[data-slot="${it.slot}"]`);
        if (icon !== null && tab !== null) flyTo(root, icon, tab, land); else land();
      });
    } else {
      go = btn("abtn blue go", h("span", "disp", c.needLevel !== null ? `${name} needs Lv ${c.needLevel}` : refusal(String(c.canWear))), ico("chev", 16));
      go.addEventListener("click", (e) => { e.stopPropagation(); if (c.needLevel !== null) ctx.openTowers(); });
    }
    box.append(h("div", "acts", details, go));
    box.addEventListener("click", (e) => e.stopPropagation());
    return box;
  }

  onRefresh(panel, () => render());
  render(focusItem !== undefined);
}
